import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";
import { SEARCH_DELAY } from "../../constant";

/**
 * Load all products and make it possible to manage them
 */
class ProductManagerStore {
    private sdk: ApplicationSdk;

    public productListLoading: boolean = false;
    public productList: Array<any> = [];
    public sortOrder: number = 1;
    public sortField: string = 'article_number';
    public searchValue: string = '';

    public available_categories: Array<{ id: number; name: string }> = []; // List of available managers
    public filterCategories: Array<{ id: number; name: string }> = []; // List of available managers
    public productDeleteConfirmation: any = null;

    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;

    public timeout: any = null;
    public s3url: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            productListLoading: observable,
            productList: observable,
            s3url: observable,
            sortOrder: observable,
            sortField: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            available_categories: observable,
            filterCategories: observable,
            productDeleteConfirmation: observable,
            onDeleteProduct: action,
            onDeleteProductCancel: action,
            onDeleteProductConfirm: action,
            updateSearch: action,
            onSort: action,
            addFilterCategory: action,
            onPageChange: action,
            clearSearch: action,
            fetchProductList: action,
            removeFilterCategory: action,
            allProducts: computed
        });

        this.sdk = sdk;

        // Load state from localStorage
        this.loadFromLocalStorage();
    }

    private saveToLocalStorage(): void {
        const state = {
            sortOrder: this.sortOrder,
            sortField: this.sortField,
            searchValue: this.searchValue,
            filterCategories: this.filterCategories
        };
        localStorage.setItem('product', JSON.stringify(state));
    }

    private loadFromLocalStorage(): void {
        const savedState = localStorage.getItem('product');
        if (savedState) {
            try {
                const { sortOrder, sortField, searchValue, filterCategories } = JSON.parse(savedState);
                runInAction(() => {
                    this.sortOrder = sortOrder || 1;
                    this.sortField = sortField || 'id';
                    this.searchValue = searchValue || '';
                    this.filterCategories = filterCategories || [];
                });
            } catch (error) {
                console.error('Failed to load state from localStorage:', error);
            }
        }
    }

    public addFilterCategory(category: any): void {
        this.filterCategories = category;
        this.saveToLocalStorage();
    }

    public applyFilterCategory(): void {
        this.fetchProductList();
    }

    public removeFilterCategory(category: any): void {
        this.filterCategories = this.filterCategories.filter((cat: any) => cat.id !== category.id);
        this.saveToLocalStorage();
        this.fetchProductList();
    }

    public onPageChange(nextPage: number): void {
        this.fetchProductList(nextPage);
    }

    public async init(): Promise<void> {
        this.fetchProductList();
    }

    public async onSearch(): Promise<void> {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        await this.fetchProductList();
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchProductList();
        }
    }

    public onDeleteProduct(product: any): void {
        this.productDeleteConfirmation = product;
    }

    public onDeleteProductCancel(): void {
        this.productDeleteConfirmation = null;
    }

    public async onDeleteProductConfirm(): Promise<void> {
        if (this.productDeleteConfirmation?.id) {
            const result = await this.sdk.fetchApi(
                'product',
                'delete',
                {
                    product_id: this.productDeleteConfirmation.id
                });
            if (result?.status === 200) {
                this.fetchProductList();
            }
        }
        this.productDeleteConfirmation = null;
    }

    public updateSearch(value: string): void {
        this.searchValue = value;

        // Save updated state to localStorage
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchProductList();
            }, SEARCH_DELAY);
        }
    }

    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;

            // Save updated state to localStorage
            this.saveToLocalStorage();
        });

        await this.fetchProductList();
    }

    public async fetchProductList(page: number = 1): Promise<void> {
        this.latestPage = page;

        runInAction(() => {
            this.productListLoading = true;
        });
        const productListResult: any = await this.sdk.fetchApi(
            'product',
            'query',
            {
                sortvalue: this.sortField,
                sort: this.sortOrder === 1 ? 'asc' : 'desc',
                search: this.searchValue || '',
                page,
                ...(
                    this.filterCategories.length > 0 ? {
                        filter: {
                            type: 'categories',
                            valueArray: this.filterCategories.map((cat: any) => cat.id)
                        }
                    } : {}
                )
            });

        runInAction(() => {
            this.productListLoading = false;

            if (productListResult?.status === 200) {
                this.productList = productListResult.json.data;
                this.currentPage = productListResult.json.pagination.page;
                this.totalCount = productListResult.json.pagination.total;
                this.maxPage = Math.ceil(this.totalCount / productListResult.json.pagination.pageSize);
                this.s3url = productListResult.json?.s3url || '';
            }
        });
    }

    public async fetchAvailableCategories(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_categories.length > 1) {
                return;
            }
        }
        const categoryResponse: any = await this.sdk.fetchApi('category', 'query', {});

        runInAction(() => {
            if (categoryResponse?.status === 200) {
                this.available_categories = categoryResponse.json.categories.map((cat: any) => ({
                    id: cat.id,
                    name: cat.name
                }));
            }
        });
    }

    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (dialog === 'product' && event?.event === 'SAVED') {
            await this.fetchProductList(this.latestPage);
        }
    }

    get allProducts(): Array<any> | null {
        if (!this.productList) return null;
        return this.productList.filter((item: any) => {
            return true;
            // return item.name.toLowerCase().includes(this.productQuickSearch.toLowerCase()) || item?.description?.toLowerCase()?.includes(this.productQuickSearch.toLowerCase());
        }).map((item: any) => ({
            ...item,
            image: item.image ? `${this.s3url}${item.image}.jpeg` : null
        }));
    }

    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
}

export default ProductManagerStore;